/**
 * EmptyState Component
 *
 * Reusable component for displaying empty states with icon, title, description,
 * and optional call-to-action button.
 */

import { type LucideIcon } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';

export type EmptyStateProps = {
  /** Icon to display */
  icon: LucideIcon;
  /** Main title text */
  title: string;
  /** Description text */
  description: string;
  /** Optional CTA button */
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  /** Additional CSS classes */
  className?: string;
  /** Test ID for E2E testing */
  testId?: string;
};

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  testId,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-8 text-center text-muted-foreground',
        className
      )}
      data-testid={testId}
    >
      <Icon className="h-10 w-10 mb-3 opacity-30" aria-hidden="true" />
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs mt-1 max-w-[200px] text-pretty">{description}</p>
      {action && (
        <Button
          variant="outline"
          size="sm"
          className="mt-4 active:scale-[0.96] transition-[color,box-shadow,scale]"
          onClick={action.onClick}
          data-testid={testId ? `${testId}-action` : undefined}
        >
          {action.icon && <action.icon className="h-4 w-4 mr-2" />}
          {action.label}
        </Button>
      )}
    </div>
  );
}
